import {
  BehaviorSubject,
  catchError,
  delay,
  filter,
  from,
  mergeMap,
  Observable,
  of,
} from 'rxjs';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action, AnyAction } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import { signOut } from '../../../state/authentication/authentication.action';
import {
  getUserProfile,
  getUserProfileFailure,
  getUserProfileSuccess,
} from '../../../state/user/user.action';
import {
  GetUserProfile,
  GET_USER_PROFILE,
} from '../../../state/user/user.action.type';
import {
  TokenExpiredError,
  TokenInvalidError,
} from '../../errors/authentication.error';
import { AUTH_API_ERROR_MESSAGES } from '../../errors/gateway.errorMessages/authentication.error.messages';
import { USER_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/userUsecase.error.messages';
import { UserUnauthorizedError } from '../../errors/user.error';
import { UserProfile } from '../../models/user.model';

const DEFAULT_RETRY_DELAY = 300;

export const getUserProfileUsecase = (
  action$: Observable<Action>,
  _state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> =>
  action$.pipe(
    filter(
      (action: AnyAction): action is GetUserProfile =>
        action.type === GET_USER_PROFILE,
    ),
    mergeMap((action: GetUserProfile) =>
      from(dependencies.userGateway.getUserProfile(action.payload.token)).pipe(
        mergeMap((userProfile: UserProfile) => {
          return [getUserProfileSuccess(userProfile)];
        }),
        catchError((error: Error) => {
          console.warn('getUserProfileUsecase failed:', error.message);
          const remainingRetries = action.payload.retry
            ? action.payload.retry - 1
            : 0;
          if (remainingRetries) {
            return of(
              getUserProfile({
                token: action.payload.token,
                retry: remainingRetries,
              }),
            ).pipe(delay(DEFAULT_RETRY_DELAY));
          }
          // Check for UserUnauthorizedError
          if (error instanceof UserUnauthorizedError) {
            return of(
              getUserProfileFailure(USER_USECASE_ERROR_MESSAGES.UNAUTHORIZED),
              signOut(),
            );
          }

          // Check for TokenExpiredError or TokenInvalidError
          if (
            error instanceof TokenExpiredError ||
            error instanceof TokenInvalidError
          ) {
            return of(
              getUserProfileFailure(AUTH_API_ERROR_MESSAGES.TOKEN_EXPIRED),
              signOut(),
            );
          }
          // General failure case
          return of(
            getUserProfileFailure(USER_USECASE_ERROR_MESSAGES.RETRIEVAL_FAILED),
          );
        }),
      ),
    ),
  );
